import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { AddPhotoAlternate as AddPhotoAlternateIcon } from '@material-ui/icons';

import { createProxyUrl } from '../../secure-portal/documents/Document.hook';
import { InputAcceptType, PresignedUrl } from '../api/File.interface';
import { Attachment } from '../attachment/Attachment.interface';
import { InlineFileValue } from './InlineFile';
import { useFileUpload, useFilterImageAttachments } from './InlineHtml.hooks';

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  .MuiSvgIcon-root {
    margin-right: 10px;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  margin-top: 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
`;

export interface InlineHtmlImageDialogProps {
  open: boolean;
  attachments: Attachment[];
  onCancel?: () => void;
  onSelect?: (url: string) => void;
  onCompleteUpload?: () => void;
  onChangeAttachments?: (attachments: InlineFileValue[]) => void;
}

const InlineHtmlImageDialog: React.FC<InlineHtmlImageDialogProps> = (
  props: InlineHtmlImageDialogProps
) => {
  const {
    open,
    attachments,
    onCancel,
    onSelect: onSelectProp,
    onCompleteUpload,
    onChangeAttachments
  } = props;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onSelectStorageKey = useCallback(
    (storageKey: string | undefined) => {
      if (onSelectProp) {
        const url: URL = createProxyUrl(storageKey || '', undefined, true);
        onSelectProp(url.toString());
      }
    },
    [onSelectProp]
  );

  const { handleFile, isLoading } = useFileUpload({
    uploadPath: 'attachments',
    inputType: InputAcceptType.Images,
    fileValues: attachments,
    onSuccess: onCompleteUpload,
    onChange: (attachments: InlineFileValue[], presignedUrl?: PresignedUrl) => {
      onChangeAttachments?.(attachments);
      if (presignedUrl) {
        onSelectStorageKey(presignedUrl.storageKey);
      }
    }
  });

  const imageAttachments: Attachment[] = useFilterImageAttachments(attachments);

  return (
    <Dialog open={open} onClose={onCancel}>
      {isLoading && <LinearProgress />}
      <StyledDialogTitle disableTypography>
        <AddPhotoAlternateIcon />
        <Typography variant='h6'>Attach image or use existing image</Typography>
      </StyledDialogTitle>
      <DialogContent>
        <Alert severity='info'>
          Upload a new image or use an existing image to attach
        </Alert>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Attach</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!imageAttachments.length && (
                <TableRow>
                  <TableCell colSpan={2}>No image attachments.</TableCell>
                </TableRow>
              )}
              {imageAttachments.map((attachment: Attachment, index: number) => (
                <TableRow key={index}>
                  <TableCell>{attachment?.name || ''}</TableCell>
                  <TableCell>
                    <Button
                      color='primary'
                      variant='outlined'
                      disabled={isLoading}
                      onClick={() => onSelectStorageKey(attachment.url)}
                    >
                      Use
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          color='primary'
          variant='contained'
          disabled={isLoading}
          endIcon={
            isLoading ? (
              <CircularProgress color='primary' size={20} />
            ) : (
              <AddPhotoAlternateIcon />
            )
          }
          onClick={() => fileInputRef.current?.click()}
        >
          Upload new image
        </Button>
        <input
          hidden
          type='file'
          ref={fileInputRef}
          accept={InputAcceptType.Images}
          onChange={event => handleFile(event?.target.files)}
        />
      </DialogActions>
    </Dialog>
  );
};

export default InlineHtmlImageDialog;
